import { Checklist as ChecklistIcon, Medication as MedicationIcon } from '@mui/icons-material';

import type { AddonOnboardingConfig } from '../types';

/**
 * Prescribing Proxy onboarding — first-run flow for a user who has just been
 * granted the proxy capability (operations_support role + the
 * `can_prepare_prescriptions_on_behalf` membership flag, GDN-1119). They prep
 * prescriptions for linked prescribers to review and send. Mirrors the
 * subscription-redesign prototype: welcome → register (DoseSpot details, no
 * NPI/DEA) → how it works.
 *
 * The registration form's field `name`s match the backend `DoseSpotProxy` payload
 * keys 1:1, so the values map drops straight into the setup-proxy mutation
 * (see ProxyOnboarding container). `email` is not collected here — it's the
 * account email, injected by the container. The welcome step's prescriber list is
 * also injected at runtime from useConnectedPrescribers (see
 * buildProxyOnboardingConfig); the static config has no bullets there.
 */
export const PROXY_ONBOARDING_CONFIG: AddonOnboardingConfig = {
  id: 'prescribing-proxy',
  capabilityName: 'eprescribing_proxy',
  branding: {
    name: 'Prescribing Proxy',
    icon: MedicationIcon,
  },
  steps: [
    {
      kind: 'education',
      nextLabel: 'Next',
      slide: {
        icon: MedicationIcon,
        title: 'Welcome to JotPsych',
        description:
          "You've been added as a Prescribing Proxy. You'll prepare prescriptions for your linked prescribers — they review and send.",
        // bullets (the prescriber names) are injected at runtime — see buildProxyOnboardingConfig
        callout: {
          label: 'What you can do:',
          text: 'Prepare prescriptions on behalf of your linked prescribers. They review and send each one.',
        },
      },
    },
    {
      kind: 'form',
      nextLabel: 'Complete Registration',
      form: {
        title: 'Complete your registration',
        description:
          'We need a few details to set up your prescribing access. No NPI or DEA required.',
        // Field names match the DoseSpotProxy payload keys. `email` is intentionally
        // NOT a field — it's the account email, injected by the ProxyOnboarding
        // container; the user can't edit it, so we don't render it.
        fields: [
          { name: 'first_name', label: 'First Name', required: true, width: 'half' },
          { name: 'last_name', label: 'Last Name', required: true, width: 'half' },
          { name: 'dob', label: 'Date of Birth', type: 'date', required: true, width: 'half' },
          {
            name: 'phone',
            label: 'Phone',
            type: 'tel',
            placeholder: '+1 (555) 000-0000',
            required: true,
            width: 'half',
          },
          {
            name: 'fax',
            label: 'Fax',
            type: 'tel',
            placeholder: '+1 (555) 000-0000',
            required: true,
            width: 'half',
          },
          { name: 'address', label: 'Street Address', required: true },
          { name: 'city', label: 'City', required: true, width: 'half' },
          { name: 'state', label: 'State', placeholder: 'MA', required: true, width: 'quarter' },
          { name: 'zip_code', label: 'ZIP', placeholder: '02101', required: true, width: 'quarter' },
        ],
        note: 'This information is shared with DoseSpot, our e-prescribing partner. No identity proofing or verification steps are required for your role.',
      },
    },
    {
      kind: 'education',
      nextLabel: 'Start Prescribing',
      slide: {
        icon: ChecklistIcon,
        title: 'How prescribing works',
        description: "Here's how you'll prepare prescriptions for your prescribers.",
        bullets: [
          'Open a chart and click "Rx" — DoseSpot opens with your proxy credentials',
          "Pick the prescriber you're prepping for (automatic if you only work with one)",
          'Enter the medication, dose, quantity, and pharmacy',
          'The prescription saves as pending for your prescriber to review and send',
        ],
        callout: {
          label: "You're all set.",
          text: 'No waiting for verification or approvals.',
        },
      },
    },
  ],
};

/**
 * Build the proxy onboarding config with the live prescriber names injected into
 * the welcome step's bullets. Pass the names of the org prescribers this proxy can
 * prep for (from `useConnectedPrescribers`). An empty list omits the bullets
 * entirely (omit-then-populate: the welcome copy stands on its own while the
 * prescriber list is loading or empty). Only the welcome step (index 0) is
 * touched; the how-it-works step keeps its static bullets.
 */
export function buildProxyOnboardingConfig(prescriberNames: string[]): AddonOnboardingConfig {
  return {
    ...PROXY_ONBOARDING_CONFIG,
    steps: PROXY_ONBOARDING_CONFIG.steps.map((step, index) =>
      index === 0 && step.kind === 'education'
        ? {
            ...step,
            slide: {
              ...step.slide,
              bullets: prescriberNames.length > 0 ? prescriberNames : undefined,
            },
          }
        : step,
    ),
  };
}
